import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useRef, useMemo, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Alert from '@mui/material/Alert';
// MUI components
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/store/store';
import { useUpdateCommentMutation } from 'src/services/threads/threads.service';
import { Comment, IThread, CommentType, CommentAccount } from 'src/services/threads/threads.types';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFMentionTextField } from 'src/components/hook-form';

// ----------------------------------------------------------------------

type CommentEditorProps = {
  comment: Comment;
  thread: IThread;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export default function CommentEditor({ comment, thread, open, onClose, onSuccess }: CommentEditorProps) {

  const { t } = useTranslate();

  const [updateComment, { isLoading }] = useUpdateCommentMutation();

  const users = useAppSelector((state) => state?.data?.users);

  const taggedAccountsRef = useRef<CommentAccount[]>([]);

  const CommentSchema = Yup.object().shape({
    text: Yup.string().required(t('common.validation.required', { field: t('common.comment') })),
  });

  const defaultValues = {
    text: '',
  };

  const methods = useForm({
    resolver: yupResolver(CommentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = methods;

  // Initialize form with comment data when dialog opens
  useEffect(() => {
    if (open && comment) {
      if (comment.data.activity) {
        setValue('text', comment.data.activity.text);
      } else if (comment.data.text) {
        setValue('text', comment.data.text);
      }

      // Initialize tagged accounts if they exist
      if (comment.data.tagged_accounts) {
        taggedAccountsRef.current = comment.data.tagged_accounts;
      }
    }
  }, [open, comment, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateComment({
        thread_id: thread.id,
        comment_id: comment.id,
        text: data.text.trim(),
        tagged_accounts: taggedAccountsRef.current,
      }).unwrap();

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      enqueueSnackbar(t('errors.default_error'), { variant: 'error' });
    }
  });

  // Handle modal close
  const handleClose = () => {
    reset();
    taggedAccountsRef.current = [];
    onClose();
  };

  const handleMentionChange = (accounts: CommentAccount[]) => {
    taggedAccountsRef.current = accounts || [];
  };

  const userOptions = useMemo(() => users?.map((user) => ({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
    })) || [], [users]);

  const isEditable = useMemo(() => comment.type === CommentType.ACTIVITY, [comment.type]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="edit-comment-dialog-title"
    >
      <DialogTitle id="edit-comment-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('threads.comments.edit_comment')}
        <Tooltip title={t('common.close')}>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          {!isEditable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('threads.comments.non_editable')}
            </Alert>
          )}

          <RHFMentionTextField
            autoFocus
            margin="dense"
            name="text"
            label={t('common.comment')}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            disabled={isLoading || !isEditable}
            users={userOptions as any[]}
            onMentionChange={handleMentionChange}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={isLoading || !isValid || !isEditable}
            loading={isLoading}
          >
            {t('common.save')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}